/*
 * Website admin panel (ycnp.in). Opens at /website/...
 * Completely separate from the App admin pages — it has its own sidebar and pages.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";
import PanelSwitcher from "./PanelSwitcher";
import { WAProvider, WA_CSS, useWA, cx, useDebounced, Btn } from "./ui";
import { siteLink } from "./api";

import Overview from "./pages/Overview";
import Analytics from "./pages/Analytics";
import Blogs from "./pages/Blogs";
import BlogEditor from "./pages/BlogEditor";
import Categories from "./pages/Categories";
import Pages from "./pages/Pages";
import PageEditor from "./pages/PageEditor";
import Posters from "./pages/Posters";
import Comments from "./pages/Comments";
import Users from "./pages/Users";
import Payments from "./pages/Payments";
import Plans from "./pages/Plans";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import WatchEarn from "./pages/WatchEarn";
import Settings from "./pages/Settings";
import ActivityLog from "./pages/ActivityLog";
import Managers from "./pages/Managers";

const SB_CSS = `
.ws-sb{background:radial-gradient(120% 60% at 0% 0%, rgba(34,211,238,.16), transparent 60%),radial-gradient(90% 50% at 100% 100%, rgba(99,102,241,.16), transparent 60%),linear-gradient(180deg,#0b1220 0%,#0f172a 55%,#111827 100%)}
.ws-item{transition:background-color .18s,color .18s,transform .15s}
.ws-item:hover{transform:translateX(3px)}
.ws-scroll{scrollbar-width:thin;scrollbar-color:rgba(148,163,184,.25) transparent}
@media (prefers-reduced-motion: reduce){.ws-item:hover{transform:none}}
`;

export const MENU = [
  {
    title: "",
    links: [
      { to: "overview", label: "Overview", icon: "fa-solid fa-gauge-high" },
      { to: "analytics", label: "Analytics", icon: "fa-solid fa-chart-column" },
    ],
  },
  {
    title: "Content",
    dot: "bg-cyan-400",
    links: [
      { to: "blogs", label: "Blogs", icon: "fa-solid fa-pen-nib", badge: "monetRequests" },
      { to: "categories", label: "Categories & Tags", icon: "fa-solid fa-tags" },
      { to: "pages", label: "Pages", icon: "fa-regular fa-file-lines" },
      { to: "posters", label: "Posters", icon: "fa-regular fa-images" },
      { to: "comments", label: "Comments & Likes", icon: "fa-regular fa-comments" },
    ],
  },
  {
    title: "People",
    dot: "bg-violet-400",
    links: [
      { to: "users", label: "Users", icon: "fa-solid fa-users" },
      { to: "notifications", label: "Send Message", icon: "fa-solid fa-bullhorn" },
      { to: "messages", label: "Contact Inbox", icon: "fa-regular fa-envelope", badge: "newMessages" },
      { to: "managers", label: "Admin Managers", icon: "fa-solid fa-user-shield", badge: "pendingManagers" },
    ],
  },
  {
    title: "Money",
    dot: "bg-amber-400",
    links: [
      { to: "payments", label: "Payments", icon: "fa-solid fa-indian-rupee-sign", badge: "pendingPayments" },
      { to: "plans", label: "Plans & Prices", icon: "fa-solid fa-crown" },
      { to: "watch", label: "Watch & Earn", icon: "fa-regular fa-circle-play" },
    ],
  },
  {
    title: "Website",
    dot: "bg-emerald-400",
    links: [
      { to: "settings", label: "Site Settings", icon: "fa-solid fa-sliders" },
      { to: "activity", label: "Activity Log", icon: "fa-solid fa-clock-rotate-left" },
    ],
  },
];

function Sidebar({ me, badges, onNavigate, onClose }) {
  const { pathname } = useLocation();
  const { currentColor } = useStateContext();
  const isOn = (to) => pathname === `/website/${to}` || pathname.startsWith(`/website/${to}/`);
  const total = Object.values(badges).reduce((s, n) => s + (n || 0), 0);
  const initials = String(me.name || "A").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "A";

  return (
    <div className="ws-sb h-full flex flex-col text-slate-300">
      <div className="shrink-0 px-4 pt-4">
        <div className="flex items-center gap-3">
          <Link to="/website/overview" onClick={onNavigate} className="flex-1 flex items-center gap-3 rounded-2xl bg-white px-3 py-2 shadow-lg shadow-black/20">
            <img src="/ycnp.png" alt="YCNP" style={{ height: 34 }} />
            <span className="ml-auto text-[9px] font-black uppercase tracking-[.18em] text-cyan-600 leading-tight text-right">
              Website
              <br />
              Admin
            </span>
          </Link>
          <button type="button" onClick={onClose} className="md:hidden w-9 h-9 rounded-xl bg-white/10 text-white" aria-label="Close menu">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <PanelSwitcher />
        {total > 0 && (
          <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_10px_#fbbf24]" />
            <p className="text-xs text-amber-100">
              <b className="tabular-nums">{total}</b> काम आपका इंतज़ार कर रहे हैं
            </p>
          </div>
        )}
      </div>

      <nav className="ws-scroll flex-1 overflow-y-auto px-3 pt-3 pb-4" aria-label="Website menu">
        {MENU.map((sec, si) => (
          <div key={si} className={sec.title ? "mt-4" : ""}>
            {sec.title && (
              <p className="flex items-center gap-2 px-2.5 py-1.5 text-[10.5px] font-bold uppercase tracking-[.14em] text-slate-500">
                <span className={cx("w-1.5 h-1.5 rounded-full", sec.dot)} />
                {sec.title}
              </p>
            )}
            <ul className="space-y-1 mt-1">
              {sec.links.map((l) => {
                const on = isOn(l.to);
                const n = l.badge ? badges[l.badge] || 0 : 0;
                return (
                  <li key={l.to}>
                    <Link
                      to={`/website/${l.to}`}
                      onClick={onNavigate}
                      aria-current={on ? "page" : undefined}
                      className={cx("ws-item relative flex items-center gap-3 h-11 pl-2 pr-2.5 rounded-xl text-[13.5px]", on ? "text-white font-semibold" : "text-slate-300 hover:text-white hover:bg-white/[.06]")}
                      style={on ? { background: `linear-gradient(90deg, ${currentColor}, ${currentColor}cc)`, boxShadow: `0 10px 24px -10px ${currentColor}` } : {}}
                    >
                      {on && <span className="absolute -left-3 top-2 bottom-2 w-1 rounded-r-full bg-white" />}
                      <span className={cx("w-8 h-8 rounded-lg grid place-items-center text-[13px] shrink-0", on ? "bg-white/25 text-white" : "bg-white/[.06] text-cyan-300")}>
                        <i className={l.icon} />
                      </span>
                      <span className="flex-1 truncate">{l.label}</span>
                      {n > 0 && <span className={cx("min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold grid place-items-center tabular-nums", on ? "bg-white text-slate-900" : "bg-amber-500 text-white")}>{n > 99 ? "99+" : n}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <a href={siteLink("/")} target="_blank" rel="noreferrer" className="ws-item mt-5 flex items-center gap-3 h-11 pl-2 pr-2.5 rounded-xl text-[13.5px] text-slate-300 hover:text-white hover:bg-white/[.06]">
          <span className="w-8 h-8 rounded-lg grid place-items-center bg-white/[.06] text-emerald-300"><i className="fa-solid fa-arrow-up-right-from-square" /></span>
          Website खोलें
        </a>
      </nav>

      <div className="shrink-0 p-3 border-t border-white/10">
        <div className="flex items-center gap-3 rounded-2xl bg-white/[.06] border border-white/10 p-2.5">
          <span className="w-10 h-10 rounded-xl grid place-items-center text-white text-sm font-black shrink-0" style={{ background: `linear-gradient(135deg, ${currentColor}, #6366f1)` }}>{initials}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{String(me.name || "").trim() || "Owner"}</p>
            <p className="text-[11px] text-slate-400"><i className="fa-solid fa-crown text-amber-400 text-[9px]" /> Owner • website</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const { api } = useWA();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const dq = useDebounced(q, 300);
  const ref = useRef(null);

  useEffect(() => {
    if (dq.trim().length < 2) return setRes(null);
    let alive = true;
    api("search", { q: dq.trim() }).then((r) => alive && setRes(r)).catch(() => alive && setRes(null));
    return () => {
      alive = false;
    };
  }, [dq, api]);

  useEffect(() => {
    const k = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current && ref.current.focus();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  const go = (to) => {
    setOpen(false);
    setQ("");
    nav(to);
  };
  const groups = res
    ? [
        ["Users", "fa-solid fa-user", res.users.map((u) => [u.name || u.email, u.email, `/website/users?open=${u.id}`])],
        ["Blogs", "fa-solid fa-pen-nib", res.blogs.map((b) => [b.title, b.status, `/website/blogs/${b.id}`])],
        ["Posters", "fa-regular fa-image", res.posters.map((p) => [p.title, "", `/website/posters?open=${p.id}`])],
        ["Pages", "fa-regular fa-file-lines", res.pages.map((p) => [p.title, "/" + p.slug, `/website/pages/${p.id}`])],
      ].filter((g) => g[2].length)
    : [];

  return (
    <div className="relative w-full max-w-md">
      <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] wa-muted" />
      <input
        ref={ref}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="User, blog, poster, page खोजें…"
        className="wa-input"
        style={{ paddingLeft: 34, paddingRight: 56, height: 42 }}
        aria-label="Search website"
      />
      <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold wa-muted border wa-line rounded-md px-1.5 py-0.5 hidden sm:block">Ctrl K</kbd>
      {open && q.trim().length >= 2 && (
        <div className="wa-card absolute left-0 right-0 mt-2 p-2 max-h-[60vh] overflow-y-auto wa-scroll" style={{ zIndex: 50 }}>
          {!res && <p className="text-sm wa-muted p-3">खोज रहे हैं…</p>}
          {res && !groups.length && <p className="text-sm wa-muted p-3">कुछ नहीं मिला</p>}
          {groups.map(([title, icon, items]) => (
            <div key={title} className="mb-1">
              <p className="text-[10.5px] font-bold uppercase tracking-wider wa-muted px-2 pt-2 pb-1">{title}</p>
              {items.map(([a, b, to], i) => (
                <button key={i} type="button" onMouseDown={() => go(to)} className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-slate-500/10">
                  <i className={cx(icon, "text-[12px] w-4 text-center")} style={{ color: "var(--wa-accent)" }} />
                  <span className="flex-1 min-w-0 text-sm font-medium truncate">{a}</span>
                  {b && <span className="text-[11px] wa-muted truncate max-w-[40%]">{b}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell({ me }) {
  const { api } = useWA();
  const { currentColor, currentMode } = useStateContext();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(() => window.innerWidth > 900);
  const [badges, setBadges] = useState({});
  const [flags, setFlags] = useState({});

  const loadBadges = useCallback(() => {
    api("stats")
      .then((s) => {
        setBadges({ pendingPayments: s.pendingPayments, newMessages: s.newMessages, monetRequests: s.monetRequests, pendingManagers: s.pendingManagers });
        setFlags({ maintenance: s.maintenance, announcement: s.announcement });
      })
      .catch(() => null);
  }, [api]);

  useEffect(() => {
    loadBadges();
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [loadBadges]);

  useEffect(() => {
    const t = setTimeout(loadBadges, 1200);
    if (window.innerWidth <= 900) setMenuOpen(false);
    window.scrollTo(0, 0);
    return () => clearTimeout(t);
  }, [pathname, loadBadges]);

  useEffect(() => {
    const r = () => setMenuOpen(window.innerWidth > 900);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  const closeOnMobile = () => window.innerWidth <= 900 && setMenuOpen(false);

  return (
    <div className={cx("wa-root min-h-screen", currentMode === "Dark" && "dark")} style={{ "--wa-accent": currentColor }}>
      {menuOpen && <div className="md:hidden fixed inset-0 bg-black/50" style={{ zIndex: 999 }} onClick={() => setMenuOpen(false)} />}
      <aside className={cx("fixed top-0 left-0 h-screen w-72 transition-transform duration-300", menuOpen ? "translate-x-0" : "-translate-x-full")} style={{ zIndex: 1000 }}>
        <Sidebar me={me} badges={badges} onNavigate={closeOnMobile} onClose={() => setMenuOpen(false)} />
      </aside>

      <div className={cx("min-h-screen transition-[margin] duration-300", menuOpen && "md:ml-72")}>
        <header className="sticky top-0 flex items-center gap-3 px-4 md:px-8 h-16 border-b wa-line" style={{ zIndex: 900, background: "color-mix(in srgb, var(--wa-bg) 85%, transparent)", backdropFilter: "blur(10px)" }}>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} className="w-10 h-10 rounded-xl grid place-items-center hover:bg-slate-500/10 shrink-0" style={{ color: "var(--wa-accent)" }} aria-label="Menu">
            <i className="fa-solid fa-bars text-lg" />
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            {flags.maintenance && (
              <Link to="/website/settings?tab=maintenance" className="hidden sm:inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                <i className="fa-solid fa-screwdriver-wrench" /> Maintenance ON
              </Link>
            )}
            <a href={siteLink("/")} target="_blank" rel="noreferrer">
              <Btn variant="ghost" size="sm" icon="fa-solid fa-arrow-up-right-from-square"><span className="hidden sm:inline">Site देखें</span></Btn>
            </a>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 md:py-8 max-w-[1500px] mx-auto">
          <Routes>
            <Route path="overview" element={<Overview />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="blogs" element={<Blogs />} />
            <Route path="blogs/new" element={<BlogEditor />} />
            <Route path="blogs/:id" element={<BlogEditor />} />
            <Route path="categories" element={<Categories />} />
            <Route path="pages" element={<Pages />} />
            <Route path="pages/new" element={<PageEditor />} />
            <Route path="pages/:id" element={<PageEditor />} />
            <Route path="posters" element={<Posters />} />
            <Route path="comments" element={<Comments />} />
            <Route path="users" element={<Users />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="messages" element={<Messages />} />
            <Route path="payments" element={<Payments />} />
            <Route path="plans" element={<Plans />} />
            <Route path="watch" element={<WatchEarn />} />
            <Route path="settings" element={<Settings />} />
            <Route path="activity" element={<ActivityLog />} />
            <Route path="managers" element={<Managers />} />
            <Route path="*" element={<Navigate to="/website/overview" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function WebsiteLayout() {
  const { baseUrl } = useBaseUrl();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, owner: false, me: {} });

  // Same login check as the App panel: only the main owner may open the website admin
  useEffect(() => {
    let alive = true;
    axios
      .post(`${baseUrl}/auth.php`, { action: "auth-check" }, { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true })
      .then((res) => {
        const d = res.data || {};
        // Same check as the App panel: the server says whether this browser has a logged-in admin session
        if (d.access === false) return nav("/login", { replace: true });
        // Only the main owner (same rule as the App panel sidebar): id 1, or an admin without a limited access list
        const owner = String(d.id) === "1" || !d.access;
        if (alive) setState({ loading: false, owner, me: { name: d.name || "", id: d.id } });
      })
      .catch(() => nav("/login"));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const css = useMemo(() => WA_CSS + SB_CSS, []);

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <style>{css}</style>
        <div className="text-center text-slate-500">
          <i className="fa-solid fa-spinner fa-spin text-2xl text-cyan-600" />
          <p className="mt-3 text-sm">Website admin खुल रहा है…</p>
        </div>
      </div>
    );
  }

  if (!state.owner) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-sm text-center bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
          <p className="text-4xl">🔒</p>
          <h1 className="text-lg font-bold mt-3">सिर्फ Owner के लिए</h1>
          <p className="text-sm text-slate-500 mt-2">Website admin सिर्फ मुख्य owner खोल सकते हैं।</p>
          <Link to="/dashboard" className="inline-block mt-5 px-5 py-2.5 rounded-xl bg-cyan-600 text-white font-semibold text-sm">App panel पर जाएँ</Link>
        </div>
      </div>
    );
  }

  return (
    <WAProvider>
      <style>{css}</style>
      <Shell me={state.me} />
    </WAProvider>
  );
}
